<script setup lang="ts">
import { computed } from 'vue'
import { emptyHealthBars, useServerHealthStore } from '@/stores/serverHealth'
import type { HealthBar } from '@/stores/serverHealth'

const props = defineProps<{
  serverId: number
  transitServerId?: number | null
  compact?: boolean
}>()

const health = useServerHealthStore()

const bars = computed(() => health.history[props.serverId] || emptyHealthBars())

function barTitle(bar: HealthBar): string {
  if (!bar.timeRange) return 'Нет данных'
  const suffix = bar.current ? ' — текущий час' : ''
  if (bar.total === 0) return `${bar.timeRange} — нет данных${suffix}`
  const errs = bar.failed > 0 ? `${bar.failed} ошибок из ${bar.total}` : `без ошибок (${bar.total} проверок)`
  const partial = bar.partial ? ', данных за час пока мало' : ''
  return `${bar.timeRange} — ${errs}${partial}${suffix}`
}

const transitColor = computed(() => {
  if (!props.transitServerId) return '#6c757d'
  const online = health.transitOnline(props.transitServerId)
  if (online === null) return '#6c757d'
  return online ? '#28a745' : '#dc3545'
})

const transitTitle = computed(() => {
  if (!props.transitServerId) return ''
  const online = health.transitOnline(props.transitServerId)
  if (online === null) return 'Статус транзитного сервера'
  return online ? 'Транзит: Online' : 'Транзит: Offline'
})
</script>

<template>
  <div class="flex flex-wrap items-center gap-2" :class="compact ? 'text-xs' : 'text-sm'">
    <div class="health-scale" :title="'Статус за 24 часа'">
      <div
        v-for="(bar, idx) in bars"
        :key="idx"
        class="health-scale-bar"
        :class="[bar.color, { current: bar.current }]"
        :title="barTitle(bar)"
      />
    </div>

    <span
      v-if="transitServerId"
      class="transit-indicator shrink-0"
      :style="{ backgroundColor: transitColor }"
      :title="transitTitle"
    />

    <span class="text-muted">|</span>

    <span class="badge" :class="health.onlineBadgeClass(serverId)" :title="health.onlineTitle(serverId)">
      {{ health.onlineLabel(serverId) }}
    </span>

    <span class="text-muted">|</span>

    <span class="badge" :class="health.pingBadgeClass(serverId)">
      {{ health.pingLabel(serverId) }}
    </span>
  </div>
</template>
